import momentTimezone from "moment-timezone";
import localStorageService from "../../../services/localStorage";
import { retryApiCall } from "../../../utils/retryApiCall";
import { isFeatureFlagEnabledForAuditabilityOfStudyConfiguration } from "./isFeatureFlagEnabledForAuditabilityOfStudyConfiguration";
import { isFeatureFlagEnabledForTestPortalEnvironment } from "./isFeatureFlagEnabledForTestPortalEnvironment";

/**
 * A reusable function to call a POST endpoint for sending audit logging data
 * If the endpoint call fails, it retried every 30 seconds for the next 3 minutes until it succeeds.
 *
 * @param {Object} api - Vue resource API instance
 * @param {String} studyId - the unique identifier for the study
 * @param {Object} auditDetails - object that contains the details for audit logging
 *   @param {String} auditDetails.section - name of the section where the change was made
 *   @param {String} auditDetails.element - name of the element where the change was made
 *   @param {String} auditDetails.httpMethod - http method of the the endpoint that caused the call to this function
 *   @param {String} auditDetails.url - endpoint url of the API
 *   @param {String} auditDetails.changeFrom - previous state object
 *   @param {String} auditDetails.changeTo - updated state object
 * @returns {Promise<void>} - promise that resolves when the audit details are successfully saved or rejects if unable to save after retries.
 */
const TOTAL_TIMEOUT = 180000;
const INTERVAL = 30000;

const saveStudyDetailsToLocalStorage = (studyId, studyDetails) => {
  const studyDetailsData = localStorageService.getItem("studyDetailsData") || {};
  studyDetailsData[studyId] = studyDetails;
  localStorageService.setItem("studyDetailsData", studyDetailsData);
};

export const saveAuditDetails = async (api, studyId, auditDetails) => {
  const isFeatureFlagEnabled = isFeatureFlagEnabledForAuditabilityOfStudyConfiguration();
  const isTestPortalFeatureFlagEnabled = isFeatureFlagEnabledForTestPortalEnvironment();
  // block audit call if feature flag is not enabled.
  if (!isFeatureFlagEnabled) {
    return;
  }

  const changeFromJSON = JSON.stringify(auditDetails.changeFrom);
  const changeToJSON = JSON.stringify(auditDetails.changeTo);

  if (changeFromJSON === changeToJSON) {
    return; // Do not save audit details
  }

  let study = localStorageService.getItem("studyDetailsData")[studyId];

  if (!study && studyId && studyId !== "0") {
    try {
      const studyAPIResponse = await api.getStudyDataFact(studyId);
      study = studyAPIResponse.body;
      saveStudyDetailsToLocalStorage(studyId, study);
    } catch (error) {
      // fail silently
      console.log("Error fetching study details: ", error);
    }
  }

  let studyVersionNumber = 0;
  if (study) {
    const { version, versionNumber } = study;
    if (isTestPortalFeatureFlagEnabled && typeof version === "string" && version.length > 0) {
      studyVersionNumber = version;
    } else if (typeof versionNumber === "string" && versionNumber.length > 0) {
      studyVersionNumber = versionNumber;
    } else studyVersionNumber = 0;
  }
  const studyVersionId = study && study.versionId ? study.versionId : null;
  const actionDateTime = momentTimezone.utc().format("YYYY-MM-DDTHH:mm:ss");

  const payload = {
    ...auditDetails,
    changeFrom: changeFromJSON,
    changeTo: changeToJSON,
    studyVersionId,
    actionDateTime
  };
  retryApiCall(api.saveAuditLoggingData, TOTAL_TIMEOUT, INTERVAL, studyId, studyVersionNumber, payload)
    .then(response => {
      console.log(`Successfully saved audit details: ${response.body}`);
    })
    .catch(console.error);
};
